import { CheckCircle, Languages, X } from 'lucide-react';

// Language Translation Constants and Functionality
export const SUPPORTED_LANGUAGES = {
  'en': { name: 'English', flag: '🇺🇸' },
  'es': { name: 'Español', flag: '🇪🇸' },
  'fr': { name: 'Français', flag: '🇫🇷' },
  'de': { name: 'Deutsch', flag: '🇩🇪' },
  'zh': { name: '中文', flag: '🇨🇳' },
  'ja': { name: '日本語', flag: '🇯🇵' },
  'ko': { name: '한국어', flag: '🇰🇷' },
  'ar': { name: 'العربية', flag: '🇸🇦' },
  'hi': { name: 'हिन्दी', flag: '🇮🇳' },
  'pt': { name: 'Português', flag: '🇧🇷' }
};

export const TRANSLATIONS = {
  en: {
    dashboard: 'Dashboard',
    activeGoals: 'Active Goals',
    learningPoints: 'Learning Points',
    completedLessons: 'Completed Lessons',
    achievements: 'Your Achievements',
    quickActions: 'Quick Actions',
    createNewPath: 'Create New Learning Path',
    continueCurrentPath: 'Continue Current Path',
    aiTutor: 'Chat with AI Tutor',
    vocabulary: 'Vocabulary',
    schedule: 'Study Schedule',
    uploadNotes: 'Upload Notes',
    settings: 'Settings',
    logout: 'Logout'
  },
  es: {
    dashboard: 'Panel Principal',
    activeGoals: 'Objetivos Activos',
    learningPoints: 'Puntos de Aprendizaje',
    completedLessons: 'Lecciones Completadas',
    achievements: 'Tus Logros',
    quickActions: 'Acciones Rápidas',
    createNewPath: 'Crear Nueva Ruta de Aprendizaje',
    continueCurrentPath: 'Continuar Ruta Actual',
    aiTutor: 'Chat con Tutor IA',
    vocabulary: 'Vocabulario',
    schedule: 'Horario de Estudio',
    uploadNotes: 'Subir Notas',
    settings: 'Configuración',
    logout: 'Cerrar Sesión'
  },
  fr: {
    dashboard: 'Tableau de bord',
    activeGoals: 'Objectifs Actifs',
    learningPoints: 'Points d\'Apprentissage',
    completedLessons: 'Leçons Terminées',
    achievements: 'Vos Réussites',
    quickActions: 'Actions Rapides',
    createNewPath: 'Créer un Nouveau Parcours',
    continueCurrentPath: 'Continuer le Parcours Actuel',
    aiTutor: 'Chat avec Tuteur IA',
    vocabulary: 'Vocabulaire',
    schedule: 'Horaire d\'Étude',
    uploadNotes: 'Télécharger Notes',
    settings: 'Paramètres',
    logout: 'Déconnexion'
  }
};

// Translation helper function
export const createTranslationFunction = (currentLanguage) => {
  return (key) => {
    return TRANSLATIONS[currentLanguage]?.[key] || TRANSLATIONS.en[key] || key;
  };
};

// Language Selection Modal Component
export const LanguageModal = ({ 
  showLanguageModal, 
  setShowLanguageModal, 
  currentLanguage, 
  setCurrentLanguage, 
  darkMode 
}) => {
  if (!showLanguageModal) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`max-w-md w-full p-6 rounded-3xl ${darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'} shadow-2xl`}>
        <div className="flex justify-between items-center mb-6">
          <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Select Language</h3>
          <button
            onClick={() => setShowLanguageModal(false)}
            className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors duration-300`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        <div className="space-y-2">
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
            <button
              key={code}
              onClick={() => {
                setCurrentLanguage(code);
                setShowLanguageModal(false);
              }}
              className={`w-full p-3 rounded-xl text-left transition-all duration-300 flex items-center space-x-3 ${
                currentLanguage === code
                  ? 'bg-purple-500 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="text-2xl">{lang.flag}</span>
              <span className="font-medium">{lang.name}</span>
              {currentLanguage === code && <CheckCircle className="w-5 h-5 ml-auto" />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Language Button Component
export const LanguageButton = ({ 
  currentLanguage, 
  setShowLanguageModal, 
  darkMode 
}) => (
  <button
    onClick={() => setShowLanguageModal(true)}
    className={`p-2 rounded-xl transition-all duration-300 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'} shadow-lg flex items-center space-x-2`}
  >
    <Languages className="w-4 h-4" />
    <span className="text-lg">{SUPPORTED_LANGUAGES[currentLanguage]?.flag}</span>
  </button>
);

// Language Setting Component for Settings Modal
export const LanguageSetting = ({ 
  currentLanguage, 
  setShowLanguageModal, 
  darkMode 
}) => (
  <div className="flex items-center justify-between">
    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Language</span>
    <button
      onClick={() => setShowLanguageModal(true)}
      className={`px-3 py-1 rounded-lg transition-all duration-300 ${darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'} flex items-center space-x-2`}
    >
      <span>{SUPPORTED_LANGUAGES[currentLanguage]?.flag}</span>
      <span>{SUPPORTED_LANGUAGES[currentLanguage]?.name}</span>
    </button>
  </div>
);